import React from 'react'
import { NextPage, NextPageContext } from 'next'
import dynamic from 'next/dynamic'
import { getIDeviceComponent } from '@ucheba/utils/helpers/components'
import { IObjectDevicesComponents } from '@ucheba/utils/helpers/components/types'
import { NextSeo } from 'next-seo'
import { isSubdomainMatches } from '@ucheba/utils/middlewares/defineLocationMiddleware'
import { Store } from 'redux'
import { useDispatch } from 'react-redux'
import { pageDataOfferThunks } from '@ucheba/store/page-data/offer'
import { clientProductsTariffsThunks } from '@ucheba/store/client-products/tariffs'
import { pageDataAboutPlatformThunks } from '@ucheba/store/page-data/about-platform'
import { sendRequest } from '@ucheba/utils/helpers/api'
import { pageDataHeaderOffersThunks } from '@ucheba/store/page-data/for-abiturients/headerOffers'
import { ESchoolPageTypes } from '../../school/index/types'
import { IEgeSubjectPageProps } from './types'
import { headerActions } from '../../../../components/Header/store'
import { footerActions } from '../../../../components/Footer/store'
import Custom404 from '../../../404.page'
import { getJsonLdElement } from '../../../../../utils/helpers/getJsonLdElement'
import { footerNavSchoolLinks } from '../../school/index/constants'
import { useShowPhoneAndMessengers } from '../../../../../utils/hooks/useShowPhoneAndMessengers'
import { useConvertSchoolData } from '../../school/index/bll/useConvertSchoolData'
import { subjects } from '../../../../../utils/constants/subjects'

const devicesPages: IObjectDevicesComponents = {
  desktop: dynamic(() => import('./desktop')),
  touch: dynamic(() => import('./touch')),
}

const EgeSubjectPage: NextPage<IEgeSubjectPageProps & { slug: string }> = (props) => {
  const { canonical, slug, data, notFound } = props
  const { content } = useConvertSchoolData(data, {
    type: ESchoolPageTypes.ege,
    subject: slug,
  })
  const dispatch = useDispatch()
  const isShowPhoneAndMessengers = useShowPhoneAndMessengers()

  if (!content || notFound) {
    return <Custom404 />
  }

  if (!isShowPhoneAndMessengers) {
    const item = footerNavSchoolLinks[0]
    dispatch(headerActions.showPhone(false))
    dispatch(footerActions.setShowMessengers(false))
    dispatch(footerActions.setNavLinks([item]))
  } else {
    dispatch(headerActions.showPhone(true))
    dispatch(footerActions.setShowMessengers(true))
    dispatch(footerActions.setNavLinks(footerNavSchoolLinks))
  }

  const JsonElement = getJsonLdElement(content?.seo?.jsonLd || '')

  return (
    <>
      <NextSeo
        title={content?.seo?.title}
        description={content?.seo?.description}
        canonical={canonical}
        openGraph={{
          images: [
            {
              url: content?.seo?.image || '',
              width: 1200,
              height: 630,
            },
          ],
        }}
      />

      {JsonElement}

      {getIDeviceComponent({ ...props, content }, devicesPages)}
    </>
  )
}

EgeSubjectPage.getInitialProps = async (
  ctx: NextPageContext & { store: Store }
): Promise<any> => {
  const { store, query, res } = ctx
  const { dispatch } = store
  const slug = String(query?.subject)

  if (!subjects.find((el) => el.slug === slug)) {
    if (res) {
      res.statusCode = 404
    }

    return {
      notFound: true,
    }
  }

  if (res) {
    res.statusCode = 200
  }

  const isDomainWWW = isSubdomainMatches(ctx.req, store, 'www')

  const canonical = isDomainWWW
    ? undefined
    : 'https://www.ucheba.ru/for-abiturients/school'

  dispatch(headerActions.toggleHeader(false))
  dispatch(footerActions.setColor('black'))
  dispatch(footerActions.setNavLinks(footerNavSchoolLinks))

  await dispatch<any>(pageDataOfferThunks.fetch({ ctx }))
  await dispatch(clientProductsTariffsThunks.fetch({ ctx }))
  await dispatch<any>(pageDataAboutPlatformThunks.fetch({ ctx }))

  const response = await sendRequest({ url: '/page_data/school_page', ctx })

  const data = response?.data

  if (query.offer) {
    await dispatch(pageDataHeaderOffersThunks.fetch({ ctx }))
  }

  return {
    canonical,
    slug,
    data,
  }
}

export default EgeSubjectPage
